import { useChatActions, useChatMessages } from '@ai-sdk-tools/store';
import { useApolloClient } from '@apollo/client';
import { MiniIconInformation } from '@newfront-insurance/core-ui';
import {
  Checkbox,
  cn,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@newfront-insurance/core-ui/v2';
import type { ToolUIPart } from 'ai';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, ArrowRight, ChevronLeft, ChevronRight, FileText, Loader2, Upload, X } from 'lucide-react';
import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type DragEvent,
  type KeyboardEvent,
  type SetStateAction,
} from 'react';

import { renderToolPendingState } from './tool-pending-state';
import { useFileSelection, type FileAttachment } from '../../../../hooks/use-file-selection';
import {
  buildClarificationTranscript,
  getClarificationPart,
  getRecommendedOptionId,
  getResolvedClarificationTranscript,
  isClarificationToolPart,
  normalizeClarificationInput,
  type ClarificationAnswer,
  type ClarificationQuestion,
} from '../../../../lib/clarification';
import type { UIMessageWithMetadata } from '../../../../lib/types';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { markClarificationResolvedInCache } from '../../lib/notification-helpers';

interface ClarificationToolProps {
  part: ToolUIPart;
  messageUuid: string;
}

interface ClarificationDraftAnswer {
  optionId?: string;
  optionIds?: string[];
  skipped?: boolean;
  text?: string;
  useTextAnswer?: boolean;
}

const CUSTOM_ANSWER_VALUE = '__custom__';
const STEP_TRANSITION = {
  duration: 0.2,
  ease: [0.22, 1, 0.36, 1] as const,
};

function getStepMotionProps(direction: 1 | -1, reducedMotion: boolean) {
  if (reducedMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.12, ease: 'linear' as const },
    };
  }

  const offset = 18;

  return {
    initial: { opacity: 0, x: direction > 0 ? offset : -offset },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: direction > 0 ? -offset : offset },
    transition: STEP_TRANSITION,
  };
}

function handleRadioKeyDown(event: KeyboardEvent<HTMLElement>, onSelect: () => void) {
  if (event.key === ' ') {
    event.preventDefault();
    onSelect();
  }
}

function buildInitialDraftAnswers(questions: ClarificationQuestion[]): Record<string, ClarificationDraftAnswer> {
  const drafts: Record<string, ClarificationDraftAnswer> = {};

  for (const question of questions) {
    const recommendedOptionId = getRecommendedOptionId(question);
    if (!recommendedOptionId) {
      continue;
    }

    drafts[question.questionId] =
      question.inputType === 'multi-select' ? { optionIds: [recommendedOptionId] } : { optionId: recommendedOptionId };
  }

  return drafts;
}

function getReadyFileUuids(attachments: FileAttachment[] | undefined): string[] {
  if (!attachments?.length) {
    return [];
  }
  return attachments
    .filter((attachment) => !attachment.isUploading && !attachment.uploadError && !!attachment.fileUuid)
    .map((attachment) => attachment.fileUuid!);
}

function getCurrentAnswer(
  question: ClarificationQuestion,
  draftAnswer: ClarificationDraftAnswer | undefined,
  fileAttachments?: FileAttachment[],
): ClarificationAnswer | undefined {
  if (draftAnswer?.skipped) {
    return {
      questionId: question.questionId,
      skipped: true,
    };
  }

  const trimmedText = draftAnswer?.text?.trim();

  if (question.inputType === 'file') {
    const fileUuids = getReadyFileUuids(fileAttachments);
    if (fileUuids.length > 0) {
      return {
        questionId: question.questionId,
        fileUuids,
        ...(trimmedText ? { text: trimmedText } : {}),
      };
    }
    return undefined;
  }

  if (question.inputType === 'multi-select') {
    const optionIds = draftAnswer?.optionIds?.filter(Boolean);

    if (optionIds?.length) {
      return {
        questionId: question.questionId,
        optionIds,
        text: trimmedText || undefined,
      };
    }
  }

  if (trimmedText) {
    return {
      questionId: question.questionId,
      text: trimmedText,
    };
  }

  if (draftAnswer?.optionId) {
    return {
      questionId: question.questionId,
      optionId: draftAnswer.optionId,
    };
  }

  return undefined;
}

export function ClarificationTool({ part, messageUuid }: ClarificationToolProps) {
  const apolloClient = useApolloClient();
  const messages = useChatMessages<UIMessageWithMetadata>();
  const message =
    messages.find(
      (candidate) => candidate.id === messageUuid || (candidate as { uuid?: unknown }).uuid === messageUuid,
    ) ??
    ({
      id: messageUuid,
      role: 'assistant',
      parts: [part as UIMessageWithMetadata['parts'][number]],
      metadata: { files: [] },
    } satisfies UIMessageWithMetadata);
  const { addToolResult } = useChatActions<UIMessageWithMetadata>();
  const { chatUuid, isPublicMode, isReadOnly } = useChatOverlayContext();
  const isInputAvailableClarificationPart = isClarificationToolPart(part) && part.state === 'input-available';
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answersByQuestionId, setAnswersByQuestionId] = useState<Record<string, ClarificationDraftAnswer>>(() => {
    if (!isInputAvailableClarificationPart) {
      return {};
    }
    return buildInitialDraftAnswers(normalizeClarificationInput(part.input).questions);
  });
  const [fileAttachmentsByQuestionId, setFileAttachmentsByQuestionId] = useState<Record<string, FileAttachment[]>>({});
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [stepDirection, setStepDirection] = useState<1 | -1>(1);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const customAnswerInputRef = useRef<HTMLInputElement>(null);
  const choiceRefs = useRef<Array<HTMLDivElement | null>>([]);
  const recommendedInitAttemptedRef = useRef(isInputAvailableClarificationPart);
  const activeFileQuestionIdRef = useRef<string | undefined>(undefined);
  const prefersReducedMotion = useReducedMotion() === true;

  const setCurrentFileAttachments = useCallback<Dispatch<SetStateAction<FileAttachment[]>>>((update) => {
    const questionId = activeFileQuestionIdRef.current;
    if (!questionId) {
      return;
    }
    setFileAttachmentsByQuestionId((prev) => {
      const existing = prev[questionId] ?? [];
      const next = typeof update === 'function' ? update(existing) : update;
      return { ...prev, [questionId]: next };
    });
  }, []);

  const {
    fileInputRef: fileQuestionInputRef,
    handleFileUpload: handleFileQuestionUpload,
    handleAttachmentClick: handleFileQuestionBrowse,
    handleDrop: handleFileQuestionDropFromHook,
    supportedFileTypes,
  } = useFileSelection({
    setAttachments: setCurrentFileAttachments,
    chatUuid,
  });

  if (
    isInputAvailableClarificationPart &&
    !recommendedInitAttemptedRef.current &&
    Object.keys(answersByQuestionId).length === 0
  ) {
    recommendedInitAttemptedRef.current = true;
    const initial = buildInitialDraftAnswers(normalizeClarificationInput(part.input).questions);
    if (Object.keys(initial).length > 0) {
      setAnswersByQuestionId(initial);
    }
  }

  if (!isClarificationToolPart(part)) {
    return null;
  }

  const clarificationToolPart = part;
  const questions = normalizeClarificationInput(clarificationToolPart.input).questions;

  const latestClarificationPart = getClarificationPart(message);
  const currentQuestion = questions[currentQuestionIndex];
  const currentDraftAnswer = currentQuestion ? answersByQuestionId[currentQuestion.questionId] : undefined;
  const currentFileAttachments = currentQuestion ? (fileAttachmentsByQuestionId[currentQuestion.questionId] ?? []) : [];
  const customAnswerSelected =
    currentDraftAnswer?.useTextAnswer === true || Boolean(currentDraftAnswer?.text?.trim().length);
  const isLatestClarification = latestClarificationPart?.toolCallId === clarificationToolPart.toolCallId;
  const isOptionQuestion = currentQuestion?.inputType === 'select' || currentQuestion?.inputType === 'multi-select';
  const isMultiSelectQuestion = currentQuestion?.inputType === 'multi-select';
  const isFileQuestion = currentQuestion?.inputType === 'file';
  const customChoiceIndex = isOptionQuestion ? (currentQuestion?.options?.length ?? -1) : -1;
  const singleSelectValue = !isMultiSelectQuestion
    ? customAnswerSelected
      ? CUSTOM_ANSWER_VALUE
      : currentDraftAnswer?.optionId
    : undefined;

  activeFileQuestionIdRef.current = currentQuestion?.questionId;

  if (clarificationToolPart.state === 'input-streaming') {
    return renderToolPendingState('clarification');
  }

  if (clarificationToolPart.state === 'output-available') {
    const transcript = getResolvedClarificationTranscript(message, clarificationToolPart);

    if (!transcript) {
      return null;
    }

    return (
      <div className="my-3 ml-auto max-w-full">
        <div className="flex flex-row items-start gap-2">
          <div className="bg-muted flex max-w-full flex-col gap-4 rounded-[20px] rounded-br-[0px] px-5 py-2">
            <div className="text-steel-500 text-[14px] leading-[26px] whitespace-pre-wrap">{transcript}</div>
          </div>
        </div>
      </div>
    );
  }

  if (clarificationToolPart.state !== 'input-available' || !isLatestClarification) {
    return null;
  }

  const currentAnswer = currentQuestion
    ? getCurrentAnswer(currentQuestion, currentDraftAnswer, currentFileAttachments)
    : undefined;
  const hasMessageText = message.parts.some((messagePart) => messagePart.type === 'text');

  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const allAnswersResolved = questions.every(
    (question) =>
      !!getCurrentAnswer(
        question,
        answersByQuestionId[question.questionId],
        fileAttachmentsByQuestionId[question.questionId],
      ),
  );
  const hasUploadingFiles = currentFileAttachments.some((attachment) => attachment.isUploading);

  if (!currentQuestion) {
    return null;
  }

  const updateCurrentQuestion = (updates: ClarificationDraftAnswer) => {
    if (isReadOnly) {
      return answersByQuestionId;
    }

    const nextAnswersByQuestionId = {
      ...answersByQuestionId,
      [currentQuestion.questionId]: {
        ...answersByQuestionId[currentQuestion.questionId],
        ...updates,
      },
    };

    setAnswersByQuestionId((previousAnswers) => ({
      ...previousAnswers,
      [currentQuestion.questionId]: {
        ...previousAnswers[currentQuestion.questionId],
        ...updates,
      },
    }));

    return nextAnswersByQuestionId;
  };

  const handleSelectOption = (optionId: string) => {
    return updateCurrentQuestion({
      optionId,
      optionIds: [],
      skipped: false,
      text: '',
      useTextAnswer: false,
    });
  };

  const handleToggleOption = (optionId: string) => {
    const existingOptionIds = currentDraftAnswer?.optionIds ?? [];
    const optionIds = existingOptionIds.includes(optionId)
      ? existingOptionIds.filter((existingOptionId) => existingOptionId !== optionId)
      : [...existingOptionIds, optionId];

    return updateCurrentQuestion({
      optionId: undefined,
      optionIds,
      skipped: false,
      text: currentDraftAnswer?.text ?? '',
      useTextAnswer: currentDraftAnswer?.useTextAnswer ?? false,
    });
  };

  const handleEnableCustomAnswer = () => {
    const preserveSelections = currentQuestion.inputType === 'multi-select';

    return updateCurrentQuestion({
      optionId: undefined,
      optionIds: preserveSelections ? (currentDraftAnswer?.optionIds ?? []) : [],
      skipped: false,
      text: currentDraftAnswer?.text ?? '',
      useTextAnswer: true,
    });
  };

  const handleTextAnswerChange = (text: string) => {
    if (isReadOnly) {
      return;
    }

    const preserveSelections = currentQuestion.inputType === 'multi-select';

    updateCurrentQuestion({
      optionId: undefined,
      optionIds: preserveSelections ? (currentDraftAnswer?.optionIds ?? []) : [],
      skipped: false,
      text,
      useTextAnswer: true,
    });
  };

  const handleGoBack = () => {
    setStepDirection(-1);
    setCurrentQuestionIndex((index) => Math.max(0, index - 1));
  };

  const handleGoForward = () => {
    setStepDirection(1);
    setCurrentQuestionIndex((index) => Math.min(questions.length - 1, index + 1));
  };

  const advanceOrSubmit = async (nextAnswersByQuestionId = answersByQuestionId) => {
    if (isReadOnly) {
      handleGoForward();
      return;
    }

    if (!isLastQuestion) {
      handleGoForward();
      return;
    }

    const orderedAnswers = questions
      .map((question) =>
        getCurrentAnswer(
          question,
          nextAnswersByQuestionId[question.questionId],
          fileAttachmentsByQuestionId[question.questionId],
        ),
      )
      .filter((answer): answer is ClarificationAnswer => !!answer);

    if (orderedAnswers.length !== questions.length) {
      return;
    }

    setIsFinalizing(true);

    const clarificationOutput = {
      answers: orderedAnswers,
      transcript: buildClarificationTranscript(questions, orderedAnswers),
    };

    try {
      await addToolResult({
        tool: 'clarification',
        toolCallId: clarificationToolPart.toolCallId,
        output: clarificationOutput,
      });

      if (!isPublicMode) {
        markClarificationResolvedInCache({
          apolloClient,
          chatUuid,
          messageUuid,
          message,
          toolCallId: clarificationToolPart.toolCallId,
          output: clarificationOutput,
          transcript: clarificationOutput.transcript,
        });
      }
    } catch (error) {
      setIsFinalizing(false);
      throw error;
    }
  };

  const handleContinue = (nextAnswersByQuestionId = answersByQuestionId) => {
    const nextCurrentAnswer = getCurrentAnswer(
      currentQuestion,
      nextAnswersByQuestionId[currentQuestion.questionId],
      fileAttachmentsByQuestionId[currentQuestion.questionId],
    );

    if (!nextCurrentAnswer || isFinalizing) {
      return;
    }

    void advanceOrSubmit(nextAnswersByQuestionId);
  };

  const focusChoice = (choiceIndex: number) => {
    window.requestAnimationFrame(() => {
      if (choiceIndex === customChoiceIndex) {
        customAnswerInputRef.current?.focus();
        return;
      }

      choiceRefs.current[choiceIndex]?.focus();
    });
  };

  const handleSelectChoiceByIndex = (choiceIndex: number) => {
    if (!currentQuestion.options) {
      return answersByQuestionId;
    }

    if (choiceIndex === customChoiceIndex) {
      return handleEnableCustomAnswer();
    }

    const optionId = currentQuestion.options[choiceIndex]?.optionId;
    if (!optionId) {
      return answersByQuestionId;
    }

    return currentQuestion.inputType === 'multi-select' ? handleToggleOption(optionId) : handleSelectOption(optionId);
  };

  const moveChoiceSelection = (choiceIndex: number, direction: -1 | 1) => {
    if (!currentQuestion.options || isFinalizing) {
      return;
    }

    const nextChoiceIndex = Math.min(customChoiceIndex, Math.max(0, choiceIndex + direction));
    if (nextChoiceIndex === choiceIndex) {
      return;
    }

    if (!isMultiSelectQuestion) {
      handleSelectChoiceByIndex(nextChoiceIndex);
    }
    focusChoice(nextChoiceIndex);
  };

  const handleChoiceKeyDown = (event: KeyboardEvent<HTMLDivElement>, choiceIndex: number) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveChoiceSelection(choiceIndex, 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveChoiceSelection(choiceIndex, -1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      const nextAnswersByQuestionId = handleSelectChoiceByIndex(choiceIndex);
      if (choiceIndex === customChoiceIndex) {
        const customText = nextAnswersByQuestionId[currentQuestion.questionId]?.text?.trim();

        if (!customText) {
          focusChoice(choiceIndex);
          return;
        }
      }

      if (isMultiSelectQuestion && choiceIndex !== customChoiceIndex) {
        return;
      }

      handleContinue(nextAnswersByQuestionId);
      return;
    }

    handleRadioKeyDown(event, () => {
      handleSelectChoiceByIndex(choiceIndex);
    });
  };

  const handleCustomAnswerInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveChoiceSelection(customChoiceIndex, -1);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveChoiceSelection(customChoiceIndex, 1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleContinue();
    }
  };

  const handleTextQuestionKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleContinue();
    }
  };

  const handleSkipQuestion = () => {
    if (isFinalizing || isReadOnly) {
      return;
    }

    const nextAnswersByQuestionId = {
      ...answersByQuestionId,
      [currentQuestion.questionId]: {
        optionId: undefined,
        optionIds: [],
        skipped: true,
        text: '',
        useTextAnswer: false,
      },
    };

    setAnswersByQuestionId(nextAnswersByQuestionId);
    if (currentQuestion.inputType === 'file') {
      setFileAttachmentsByQuestionId((prev) => {
        if (!prev[currentQuestion.questionId]) {
          return prev;
        }
        const { [currentQuestion.questionId]: _, ...rest } = prev;
        return rest;
      });
    }
    void advanceOrSubmit(nextAnswersByQuestionId);
  };

  const handleRemoveFileAttachment = (tempId: string) => {
    if (isReadOnly || isFinalizing) {
      return;
    }
    setCurrentFileAttachments((existing) => existing.filter((attachment) => attachment.tempId !== tempId));
  };

  const handleFileQuestionDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (isReadOnly || isFinalizing) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!isDraggingFile) {
      setIsDraggingFile(true);
    }
  };

  const handleFileQuestionDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleFileQuestionDrop = (event: DragEvent<HTMLDivElement>) => {
    setIsDraggingFile(false);
    if (isReadOnly || isFinalizing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    handleFileQuestionDropFromHook(event);
  };

  const continueDisabled =
    isReadOnly || isFinalizing || !currentAnswer || hasUploadingFiles || (isLastQuestion && !allAnswersResolved);
  const introCopy =
    questions.length === 1
      ? 'For best results let me ask you a follow up question:'
      : 'For best results let me ask you a few follow up questions:';

  return (
    <div className="mt-3 flex w-full flex-col gap-3">
      {!hasMessageText && <p className="text-steel-500 text-[14px] leading-[26px]">{introCopy}</p>}

      <div className="bg-aqua-citron-gradient w-full rounded-[16px] p-px">
        <motion.div layout className="bg-aqua-50 flex w-full flex-col rounded-[15px]">
          <div className="flex items-start justify-between gap-6 px-[30px] py-5">
            <div className="min-w-0 flex-1 overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={`${currentQuestion.questionId}-header`}
                  {...getStepMotionProps(stepDirection, prefersReducedMotion)}
                  className="flex min-w-0 flex-col gap-1"
                >
                  <p className="text-steel-500 text-[14px] leading-5 font-semibold">{currentQuestion.question}</p>
                  {currentQuestion.helperText && (
                    <p className="text-steel-400 text-[12px] leading-4">{currentQuestion.helperText}</p>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex shrink-0 flex-col items-center gap-[5px]">
              <div className="flex items-center gap-1">
                {questions.map((question, index) => {
                  const resolvedAnswer = getCurrentAnswer(
                    question,
                    answersByQuestionId[question.questionId],
                    fileAttachmentsByQuestionId[question.questionId],
                  );

                  return (
                    <span
                      key={question.questionId}
                      aria-hidden="true"
                      className={cn('h-2 w-2 rounded-full transition-colors', {
                        'bg-steel-400': index === currentQuestionIndex,
                        'bg-brand-400': index !== currentQuestionIndex && resolvedAnswer,
                        'bg-steel-75': index !== currentQuestionIndex && !resolvedAnswer,
                      })}
                    />
                  );
                })}
              </div>

              <div className="text-steel-400 flex items-center gap-1 text-[12px] leading-4">
                <button
                  type="button"
                  className="text-steel-400 flex size-[18px] items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={handleGoBack}
                  disabled={currentQuestionIndex === 0 || isFinalizing}
                  aria-label="Previous clarification question"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <span>{`${currentQuestionIndex + 1}/${questions.length}`}</span>
                <button
                  type="button"
                  className="text-steel-400 flex size-[18px] items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => handleContinue()}
                  disabled={isLastQuestion || isFinalizing || (!isReadOnly && !currentAnswer)}
                  aria-label="Next clarification question"
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-steel-75 border-t border-solid px-[15px] pt-[10px]">
            <div className="overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={currentQuestion.questionId}
                  {...getStepMotionProps(stepDirection, prefersReducedMotion)}
                  className="flex flex-col gap-[3px]"
                >
                  {isOptionQuestion && currentQuestion.options && (
                    <>
                      {isMultiSelectQuestion ? (
                        <div role="group" aria-label={currentQuestion.question} className="flex flex-col gap-[3px]">
                          {currentQuestion.options.map((option, optionIndex) => {
                            const optionId = option.optionId;
                            const isSelected =
                              Boolean(optionId) && (currentDraftAnswer?.optionIds?.includes(optionId) ?? false);
                            const canSelectOption = !isReadOnly && !isFinalizing && Boolean(optionId);

                            return (
                              <div
                                key={option.optionId ?? `${currentQuestion.questionId}-option-${optionIndex}`}
                                ref={(element) => {
                                  choiceRefs.current[optionIndex] = element;
                                }}
                                role="checkbox"
                                aria-checked={isSelected}
                                tabIndex={canSelectOption ? 0 : -1}
                                className={cn(
                                  'group flex w-full items-center justify-between gap-3 rounded-[20px] px-4 py-[10px] text-left transition-colors duration-150',
                                  canSelectOption && 'cursor-pointer',
                                  isSelected ? 'bg-aqua-200' : 'hover:bg-aqua-100',
                                  !canSelectOption && 'cursor-not-allowed opacity-60',
                                )}
                                onClick={() => {
                                  if (optionId) {
                                    handleToggleOption(optionId);
                                  }
                                }}
                                onKeyDown={(event) => handleChoiceKeyDown(event, optionIndex)}
                              >
                                <div className="flex min-w-0 flex-1 items-center gap-3">
                                  <Checkbox
                                    aria-hidden="true"
                                    checked={isSelected}
                                    disabled={!canSelectOption}
                                    tabIndex={-1}
                                    className="border-steel-200 data-[state=checked]:border-brand-400 data-[state=checked]:bg-brand-400 pointer-events-none h-[18px] w-[18px] rounded-[4px] bg-white"
                                  />
                                  <p
                                    className={cn(
                                      'text-[14px] leading-5',
                                      isSelected ? 'text-steel-500' : 'text-steel-400',
                                    )}
                                  >
                                    {option.label}
                                  </p>
                                </div>

                                {option.description && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          className="text-steel-200 group-hover:text-steel-300 flex size-5 shrink-0 items-center justify-center transition-colors"
                                          onClick={(event) => event.stopPropagation()}
                                          aria-label={`More information about ${option.label}`}
                                        >
                                          <MiniIconInformation color="currentColor" size={16} />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="left"
                                        align="center"
                                        sideOffset={8}
                                        className="bg-steel-500 max-w-[368px] rounded-[6px] border-0 px-2 py-1 text-[12px] leading-4 whitespace-normal text-white shadow-none"
                                      >
                                        {option.description}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            );
                          })}

                          <div
                            ref={(element) => {
                              if (customChoiceIndex >= 0) {
                                choiceRefs.current[customChoiceIndex] = element;
                              }
                            }}
                            role="checkbox"
                            aria-checked={customAnswerSelected}
                            tabIndex={isReadOnly || isFinalizing ? -1 : 0}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-[20px] px-4 py-[10px] text-left transition-colors duration-150',
                              !isReadOnly && !isFinalizing && 'cursor-pointer',
                              customAnswerSelected ? 'bg-aqua-200' : 'hover:bg-aqua-100',
                              (isReadOnly || isFinalizing) && 'cursor-not-allowed opacity-60',
                            )}
                            onClick={() => {
                              if (isReadOnly) {
                                return;
                              }

                              if (customAnswerSelected) {
                                customAnswerInputRef.current?.focus();
                                return;
                              }

                              handleEnableCustomAnswer();
                              focusChoice(customChoiceIndex);
                            }}
                            onKeyDown={(event) => handleChoiceKeyDown(event, customChoiceIndex)}
                          >
                            <Checkbox
                              aria-hidden="true"
                              checked={customAnswerSelected}
                              disabled={isReadOnly || isFinalizing}
                              tabIndex={-1}
                              className="border-steel-200 data-[state=checked]:border-brand-400 data-[state=checked]:bg-brand-400 pointer-events-none h-[18px] w-[18px] rounded-[4px] bg-white"
                            />
                            {customAnswerSelected ? (
                              <input
                                ref={customAnswerInputRef}
                                value={currentDraftAnswer?.text ?? ''}
                                onChange={(event) => handleTextAnswerChange(event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                onKeyDown={(event) => {
                                  event.stopPropagation();
                                  handleCustomAnswerInputKeyDown(event);
                                }}
                                placeholder="Start typing..."
                                aria-label={`${currentQuestion.question} custom answer`}
                                className="text-steel-500 placeholder:text-steel-300 min-w-0 flex-1 bg-transparent text-[14px] leading-5 outline-none"
                                disabled={isReadOnly || isFinalizing}
                              />
                            ) : (
                              <p className="text-steel-400 text-[14px] leading-5">Something else</p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <RadioGroup
                          value={singleSelectValue}
                          className="flex w-full flex-col gap-[3px]"
                          aria-label={currentQuestion.question}
                        >
                          {currentQuestion.options.map((option, optionIndex) => {
                            const optionId = option.optionId;
                            const isSelected = Boolean(optionId) && currentDraftAnswer?.optionId === optionId;
                            const canSelectOption = !isReadOnly && !isFinalizing && Boolean(optionId);

                            return (
                              <div
                                key={option.optionId ?? `${currentQuestion.questionId}-option-${optionIndex}`}
                                ref={(element) => {
                                  choiceRefs.current[optionIndex] = element;
                                }}
                                role="radio"
                                aria-checked={isSelected}
                                tabIndex={canSelectOption ? 0 : -1}
                                className={cn(
                                  'group flex w-full items-center justify-between gap-3 rounded-[20px] px-4 py-[10px] text-left transition-colors duration-150',
                                  canSelectOption && 'cursor-pointer',
                                  isSelected ? 'bg-aqua-200' : 'hover:bg-aqua-100',
                                  !canSelectOption && 'cursor-not-allowed opacity-60',
                                )}
                                onClick={() => {
                                  if (optionId) {
                                    handleSelectOption(optionId);
                                  }
                                }}
                                onKeyDown={(event) => handleChoiceKeyDown(event, optionIndex)}
                              >
                                <div className="flex min-w-0 flex-1 items-center gap-3">
                                  <RadioGroupItem
                                    aria-hidden="true"
                                    value={optionId ?? ''}
                                    disabled={!canSelectOption}
                                    tabIndex={-1}
                                    className="border-steel-200 data-[state=checked]:border-brand-400 pointer-events-none h-[18px] w-[18px]"
                                  />
                                  <p
                                    className={cn(
                                      'text-[14px] leading-5',
                                      isSelected ? 'text-steel-500' : 'text-steel-400',
                                    )}
                                  >
                                    {option.label}
                                  </p>
                                </div>

                                {option.description && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          className="text-steel-200 group-hover:text-steel-300 flex size-5 shrink-0 items-center justify-center transition-colors"
                                          onClick={(event) => event.stopPropagation()}
                                          aria-label={`More information about ${option.label}`}
                                        >
                                          <MiniIconInformation color="currentColor" size={16} />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="left"
                                        align="center"
                                        sideOffset={8}
                                        className="bg-steel-500 max-w-[368px] rounded-[6px] border-0 px-2 py-1 text-[12px] leading-4 whitespace-normal text-white shadow-none"
                                      >
                                        {option.description}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            );
                          })}

                          <div
                            ref={(element) => {
                              if (customChoiceIndex >= 0) {
                                choiceRefs.current[customChoiceIndex] = element;
                              }
                            }}
                            role="radio"
                            aria-checked={customAnswerSelected}
                            tabIndex={isReadOnly || isFinalizing ? -1 : 0}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-[20px] px-4 py-[10px] text-left transition-colors duration-150',
                              !isReadOnly && !isFinalizing && 'cursor-pointer',
                              customAnswerSelected ? 'bg-aqua-200' : 'hover:bg-aqua-100',
                              (isReadOnly || isFinalizing) && 'cursor-not-allowed opacity-60',
                            )}
                            onClick={() => {
                              if (isReadOnly) {
                                return;
                              }

                              if (customAnswerSelected) {
                                customAnswerInputRef.current?.focus();
                                return;
                              }

                              handleEnableCustomAnswer();
                              focusChoice(customChoiceIndex);
                            }}
                            onKeyDown={(event) => handleChoiceKeyDown(event, customChoiceIndex)}
                          >
                            <RadioGroupItem
                              aria-hidden="true"
                              value={CUSTOM_ANSWER_VALUE}
                              disabled={isReadOnly || isFinalizing}
                              tabIndex={-1}
                              className="border-steel-200 data-[state=checked]:border-brand-400 pointer-events-none h-[18px] w-[18px]"
                            />
                            {customAnswerSelected ? (
                              <input
                                ref={customAnswerInputRef}
                                value={currentDraftAnswer?.text ?? ''}
                                onChange={(event) => handleTextAnswerChange(event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                onKeyDown={(event) => {
                                  event.stopPropagation();
                                  handleCustomAnswerInputKeyDown(event);
                                }}
                                placeholder="Start typing..."
                                aria-label={`${currentQuestion.question} custom answer`}
                                className="text-steel-500 placeholder:text-steel-300 min-w-0 flex-1 bg-transparent text-[14px] leading-5 outline-none"
                                disabled={isReadOnly || isFinalizing}
                              />
                            ) : (
                              <p className="text-steel-400 text-[14px] leading-5">Something else</p>
                            )}
                          </div>
                        </RadioGroup>
                      )}
                    </>
                  )}

                  {currentQuestion.inputType === 'text' && (
                    <textarea
                      value={currentDraftAnswer?.text ?? ''}
                      onChange={(event) => handleTextAnswerChange(event.target.value)}
                      onKeyDown={handleTextQuestionKeyDown}
                      placeholder="Start typing..."
                      className={cn(
                        'border-steel-200 min-h-[120px] w-full resize-none rounded-[8px] border border-solid bg-white',
                        'text-steel-500 placeholder:text-steel-300 px-3 py-3 text-[14px] leading-5',
                        'focus:border-brand-400 outline-none',
                      )}
                      disabled={isReadOnly || isFinalizing}
                    />
                  )}

                  {isFileQuestion && (
                    <ClarificationFileInput
                      attachments={currentFileAttachments}
                      acceptedFileTypes={currentQuestion.acceptedFileTypes}
                      defaultFileTypes={supportedFileTypes}
                      maxFiles={currentQuestion.maxFiles ?? 1}
                      isDragging={isDraggingFile}
                      disabled={isReadOnly || isFinalizing}
                      onBrowse={handleFileQuestionBrowse}
                      onDragOver={handleFileQuestionDragOver}
                      onDragLeave={handleFileQuestionDragLeave}
                      onDrop={handleFileQuestionDrop}
                      onRemove={handleRemoveFileAttachment}
                      fileInputRef={fileQuestionInputRef}
                      onFileInputChange={handleFileQuestionUpload}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center justify-between px-[27px] py-5">
            <button
              type="button"
              className="text-brand-400 text-[14px] leading-5 font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleSkipQuestion}
              disabled={isReadOnly || isFinalizing}
            >
              Skip question
            </button>

            <button
              type="button"
              className={cn(
                'text-moss-500 inline-flex items-center gap-[10px] rounded-full px-4 py-2 text-[14px] leading-5 font-semibold transition-opacity',
                'bg-lime-blue-gradient',
                continueDisabled && 'cursor-not-allowed opacity-50',
              )}
              onClick={() => handleContinue()}
              disabled={continueDisabled}
            >
              <ArrowRight className="size-[18px]" />
              <span>Continue</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

interface ClarificationFileInputProps {
  attachments: FileAttachment[];
  acceptedFileTypes?: string[];
  defaultFileTypes: string[];
  maxFiles: number;
  isDragging: boolean;
  disabled: boolean;
  onBrowse: () => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onRemove: (tempId: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function buildAcceptString(acceptedFileTypes: string[] | undefined, defaultFileTypes: string[]): string {
  if (acceptedFileTypes?.length) {
    return acceptedFileTypes.map((extension) => (extension.startsWith('.') ? extension : `.${extension}`)).join(',');
  }
  return defaultFileTypes.join(',');
}

function buildAcceptHint(acceptedFileTypes: string[] | undefined, defaultFileTypes: string[]): string {
  const types = acceptedFileTypes?.length
    ? acceptedFileTypes
    : defaultFileTypes.map((entry) => entry.replace(/^\./, ''));
  return `${types.map((entry) => entry.toUpperCase()).join(', ')} · up to 50 MB`;
}

function ClarificationFileInput({
  attachments,
  acceptedFileTypes,
  defaultFileTypes,
  maxFiles,
  isDragging,
  disabled,
  onBrowse,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemove,
  fileInputRef,
  onFileInputChange,
}: ClarificationFileInputProps) {
  const acceptString = buildAcceptString(acceptedFileTypes, defaultFileTypes);
  const acceptHint = buildAcceptHint(acceptedFileTypes, defaultFileTypes);
  const hasReachedFileLimit = attachments.length >= maxFiles;
  const showDropZone = attachments.length === 0;

  return (
    <div className="flex flex-col gap-2 pb-[15px]">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptString}
        multiple={maxFiles > 1}
        onChange={onFileInputChange}
        className="hidden"
      />

      {showDropZone ? (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => {
            if (!disabled) onBrowse();
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onBrowse();
            }
          }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          aria-disabled={disabled}
          className={cn(
            'bg-aqua-100 border-aqua-200 flex h-[115px] w-full items-center justify-center rounded-[16px] border-2 border-dashed px-4 py-[10px] transition-colors',
            !disabled && 'hover:bg-aqua-200/60 cursor-pointer',
            isDragging && 'bg-aqua-200/60 border-brand-400',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <div className="flex flex-col items-center justify-center gap-1">
            <Upload className="text-steel-500 size-6" aria-hidden="true" />
            <p className="text-[14px] leading-5 font-semibold text-black">Drop a file here, or browse to upload</p>
            <p className="text-steel-400 text-[12px] leading-4 font-semibold">{acceptHint}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {attachments.map((attachment) => (
            <ClarificationFileChip
              key={attachment.tempId}
              attachment={attachment}
              disabled={disabled}
              onRemove={onRemove}
            />
          ))}

          {!hasReachedFileLimit && (
            <button
              type="button"
              onClick={onBrowse}
              disabled={disabled}
              className={cn(
                'text-brand-400 self-start text-[12px] leading-4 font-semibold',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              {`Add another file (${attachments.length}/${maxFiles})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function getFileTypeBadge(filename: string): string {
  const dotIndex = filename.lastIndexOf('.');
  const extension = dotIndex !== -1 ? filename.slice(dotIndex + 1).toLowerCase() : '';
  return extension ? extension.toUpperCase().slice(0, 4) : 'FILE';
}

function ClarificationFileChip({
  attachment,
  disabled,
  onRemove,
}: {
  attachment: FileAttachment;
  disabled: boolean;
  onRemove: (tempId: string) => void;
}) {
  const badge = getFileTypeBadge(attachment.file.name);
  const removeDisabled = disabled || attachment.isUploading;

  return (
    <div className="bg-aqua-100 flex w-full items-center gap-[10px] rounded-[16px] px-4 py-[10px]">
      <div className="bg-aqua-50 border-aqua-200 flex h-10 w-10 items-center justify-center rounded-[4px] border border-solid">
        {attachment.uploadError ? (
          <AlertCircle className="text-rebrand-400 size-4" />
        ) : attachment.isUploading ? (
          <Loader2 className="text-steel-400 size-4 animate-spin" />
        ) : (
          <FileText className="text-steel-500 size-4" />
        )}
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <p className="text-steel-500 truncate text-[14px] leading-5">{attachment.file.name}</p>
        {attachment.uploadError && (
          <span className="text-rebrand-400 shrink-0 text-[12px] leading-4">Upload failed</span>
        )}
        {!attachment.uploadError && attachment.isUploading && (
          <span className="text-steel-400 shrink-0 text-[12px] leading-4">Uploading…</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(attachment.tempId)}
        disabled={removeDisabled}
        aria-label={`Remove ${attachment.file.name}`}
        className={cn(
          'text-steel-400 hover:text-steel-500 shrink-0 transition-colors',
          removeDisabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <X className="size-3" />
      </button>
      <span className="sr-only">{`File type: ${badge}`}</span>
    </div>
  );
}
